'use client'

import {
  FormEvent,
  useState,
} from 'react'
import Image from 'next/image'
import { Button } from '@/components/Button'
import { Container } from '@/components/Container'
import { Input } from '@/components/Input'
import { Modal } from '@/components/Modal'
import { SectionDescription } from '@/components/SectionDescription'
import { SectionTitle } from '@/components/SectionTitle'
import { Select } from '@/components/Select'
import { Tag } from '@/components/Tag'
import { Textarea } from '@/components/Textarea'
import { serviceOptions } from '@/data/contact'

const successTitleId = 'contact-success-title'
const successDescriptionId = 'contact-success-description'
const requiredFieldMessage = 'Informação obrigatória.'
const invalidEmailMessage = 'Informe um e-mail válido.'
const submitErrorMessage =
  'Não foi possível enviar sua solicitação. Tente novamente em instantes.'

type ContactErrors = {
  name: string
  email: string
}

type ContactErrorResponse = {
  errors?: Partial<ContactErrors>
  message?: string
}

function formatPhone(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 11)

  if (digits.length <= 2) {
    return digits
  }

  if (digits.length <= 6) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`
  }

  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  }

  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
}

export function ContactSection() {
  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [service, setService] = useState('')
  const [description, setDescription] = useState('')
  const [acceptsEmails, setAcceptsEmails] = useState(false)
  const [errors, setErrors] = useState<ContactErrors>({
    name: '',
    email: '',
  })
  const [submitError, setSubmitError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)

  function updateRequiredField(
    field: keyof typeof errors,
    nextValue: string,
    updateValue: (value: string) => void,
  ) {
    updateValue(nextValue)
    setSubmitError('')

    if (nextValue.trim() && errors[field]) {
      setErrors((currentErrors) => ({
        ...currentErrors,
        [field]: '',
      }))
    }
  }

  function resetForm() {
    setName('')
    setCompany('')
    setEmail('')
    setPhone('')
    setService('')
    setDescription('')
    setAcceptsEmails(false)
    setErrors({
      name: '',
      email: '',
    })
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (isSubmitting) {
      return
    }

    setSubmitError('')

    const nextErrors = {
      name: name.trim() ? '' : requiredFieldMessage,
      email: email.trim()
        ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
          ? ''
          : invalidEmailMessage
        : requiredFieldMessage,
    }

    setErrors(nextErrors)

    if (nextErrors.name || nextErrors.email) {
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          company,
          email,
          phone,
          service,
          description,
          acceptsEmails,
        }),
      })

      if (!response.ok) {
        const responseBody = (await response
          .json()
          .catch(() => null)) as ContactErrorResponse | null

        if (response.status === 400 && responseBody?.errors) {
          setErrors({
            name: responseBody.errors.name || '',
            email: responseBody.errors.email || '',
          })
          return
        }

        setSubmitError(responseBody?.message || submitErrorMessage)
        return
      }

      resetForm()
      setIsSuccessModalOpen(true)
    } catch {
      setSubmitError(submitErrorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section id="contato" className="bg-[#F2F2F2] py-12 md:py-20">
      <Container>
        <div className="grid gap-8 lg:grid-cols-[minmax(320px,0.75fr)_minmax(0,1.25fr)] lg:gap-10">
          <div className="relative min-h-90 overflow-hidden rounded-3xl lg:min-h-190">
            <Image
              src="/assets/images/form-bg.webp"
              alt=""
              aria-hidden="true"
              fill
              sizes="(min-width: 1024px) 40vw, 100vw"
              className="object-cover"
            />
          </div>

          <div>
            <Tag>Fale conosco</Tag>

            <SectionTitle className="mt-5 md:mt-6">
              A Diversa Advisory leva sua empresa para o próximo nível
            </SectionTitle>
            <SectionDescription className="mt-4 text-black-300 md:mt-6">
              Estamos prontos para atuar ao seu lado e ajudar sua empresa a
              fortalecer políticas de inclusão, qualificar práticas de
              acessibilidade ou desenvolver soluções formativas.
            </SectionDescription>

            <form
              className="mt-5 grid gap-4 md:gap-3"
              noValidate
              aria-label="Formulário de contato"
              aria-busy={isSubmitting}
              onSubmit={handleSubmit}
            >
              <div>
                <Input
                  id="contact-name"
                  name="name"
                  label="Nome completo"
                  value={name}
                  required
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={errors.name ? 'contact-name-error' : undefined}
                  onValueChange={(nextValue) =>
                    updateRequiredField('name', nextValue, setName)
                  }
                />
                {errors.name ? (
                  <p
                    id="contact-name-error"
                    role="alert"
                    className="ml-5 mt-2 font-sans text-sm leading-5 text-[#9C0B0B]"
                  >
                    {errors.name}
                  </p>
                ) : null}
              </div>

              <Input
                id="contact-company"
                name="company"
                label="Órgão/Empresa"
                value={company}
                onValueChange={setCompany}
              />

              <div>
                <Input
                  id="contact-email"
                  name="email"
                  type="email"
                  label="E-mail"
                  value={email}
                  required
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={
                    errors.email ? 'contact-email-error' : undefined
                  }
                  onValueChange={(nextValue) =>
                    updateRequiredField('email', nextValue, setEmail)
                  }
                />
                {errors.email ? (
                  <p
                    id="contact-email-error"
                    role="alert"
                    className="ml-5 mt-2 font-sans text-sm leading-5 text-[#9C0B0B]"
                  >
                    {errors.email}
                  </p>
                ) : null}
              </div>

              <Input
                id="contact-phone"
                name="phone"
                type="tel"
                inputMode="tel"
                label="Telefone"
                value={phone}
                onValueChange={(nextValue) => setPhone(formatPhone(nextValue))}
              />

              <Select
                id="contact-service"
                name="service"
                label="Serviço de interesse"
                options={serviceOptions}
                value={service}
                onChange={setService}
              />

              <Textarea
                id="contact-description"
                name="description"
                label="Descrição"
                value={description}
                onValueChange={setDescription}
              />

              <div className="grid gap-5 pt-2 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                <div className="flex gap-3 font-sans text-sm leading-5 text-black-300">
                  <input
                    type="hidden"
                    name="acceptsEmails"
                    value={acceptsEmails ? 'true' : 'false'}
                  />
                  <label
                    htmlFor="contact-accepts-emails"
                    className="relative -m-2 flex size-11 shrink-0 cursor-pointer items-center justify-center"
                  >
                    <input
                      id="contact-accepts-emails"
                      type="checkbox"
                      checked={acceptsEmails}
                      aria-labelledby="contact-accepts-emails-label"
                      aria-describedby="contact-accepts-emails-policy"
                      className="peer sr-only"
                      onChange={(event) =>
                        setAcceptsEmails(event.currentTarget.checked)
                      }
                    />
                    <span
                      aria-hidden="true"
                      className="block size-6 rounded-full border border-transparent bg-white-50 transition-colors peer-checked:border-lightning-yellow-500 peer-focus-visible:ring-2 peer-focus-visible:ring-lightning-yellow-600 peer-focus-visible:ring-offset-2"
                    />
                    <span
                      aria-hidden="true"
                      className="absolute left-1/2 top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-lightning-yellow-500 opacity-0 transition-opacity peer-checked:opacity-100"
                    />
                  </label>
                  <span>
                    <label
                      id="contact-accepts-emails-label"
                      htmlFor="contact-accepts-emails"
                      className="cursor-pointer"
                    >
                      Aceito receber e-mails com ofertas e conteúdos.
                    </label>{' '}
                    <span id="contact-accepts-emails-policy">
                      Prometemos não utilizar suas informações para spam,{' '}
                    </span>
                    <a
                      href="/politica-privacidade.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="clique aqui e veja nossa Política de Dados (PDF, abre em uma nova aba)"
                      className="font-bold text-white-950 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-lightning-yellow-600 focus-visible:ring-offset-2"
                    >
                      clique aqui e veja nossa Política de Dados
                    </a>
                    .
                  </span>
                </div>

                <Button
                  type="submit"
                  className="w-full md:w-auto"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enviando...' : 'Solicitar orçamento'}
                </Button>
              </div>
            </form>
            {submitError ? (
              <p
                role="alert"
                className="mt-4 font-sans text-sm leading-5 text-[#9C0B0B]"
              >
                {submitError}
              </p>
            ) : null}
            {isSuccessModalOpen ? (
              <p className="sr-only" aria-live="polite">
                Recebemos sua solicitação.
              </p>
            ) : null}
          </div>
        </div>
      </Container>

      <Modal
        isOpen={isSuccessModalOpen}
        titleId={successTitleId}
        descriptionId={successDescriptionId}
        onClose={() => setIsSuccessModalOpen(false)}
      >
        <div className="flex flex-col items-center text-center">
          <div className="flex size-16 md:size-20 items-center justify-center rounded-full bg-[#159404]">
            <Image
              src="/assets/icons/check.svg"
              alt=""
              width={40}
              height={40}
              aria-hidden="true"
              className="size-8 md:size-10 invert"
            />
          </div>

          <h2
            id={successTitleId}
            className="mt-8 font-heading font-bold leading-8 text-2xl md:text-[32px] md:leading-10 text-black-950"
          >
            Recebemos sua solicitação!
          </h2>

          <p
            id={successDescriptionId}
            className="mt-3 font-heading text-xl md:text-2xl font-normal leading-8 text-black-300"
          >
            Vamos construir essa agenda juntos! Entraremos em contato o mais
            rápido possível para seguirmos conversando sobre o momento da sua
            empresa.
          </p>

          <Button
            className="mt-8 w-full md:w-auto"
            onClick={() => setIsSuccessModalOpen(false)}
          >
            Fechar
          </Button>
        </div>
      </Modal>
    </section>
  )
}
